import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Store, Eye, CheckCircle, XCircle, Clock, Ban,
  MapPin, Phone, Loader, X,
  RefreshCw, Star,
  Archive, Trash2, Tag, Globe
} from 'lucide-react';
import adminService, {
  Business, BusinessStatus, GetBusinessesParams,
  Category
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageHelper';
import DataTable from '../../components/common/DataTable';

const BusinessManagement: React.FC = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [totalBusinesses, setTotalBusinesses] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    active: 0,
    suspended: 0
  });

  // Modals
  const [approveModal, setApproveModal] = useState<{ show: boolean; business: Business | null }>({
    show: false,
    business: null
  });
  const [rejectModal, setRejectModal] = useState<{ show: boolean; business: Business | null }>({
    show: false,
    business: null
  });
  const [suspendModal, setSuspendModal] = useState<{ show: boolean; business: Business | null }>({
    show: false,
    business: null
  });
  const [reassignModal, setReassignModal] = useState<{ show: boolean; business: Business | null }>({
    show: false,
    business: null
  });
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('');

  // Form states
  const [rejectionReason, setRejectionReason] = useState('');
  const [suspensionReason, setSuspensionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedBusinesses, setSelectedBusinesses] = useState<number[]>([]);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);

  const openReassignModal = (business: Business) => {
    setReassignModal({ show: true, business });
    setSelectedCategoryId(business.business_category_id || '');
  };

  const handleReassignCategory = async () => {
    if (!reassignModal.business || !selectedCategoryId) {
      toast.error('Please select a category');
      return;
    }

    if (Number(selectedCategoryId) === reassignModal.business.business_category_id) {
      toast.error('Business is already in this category');
      return;
    }

    try {
      setProcessing(true);
      await adminService.updateBusinessDetails({
        business_id: reassignModal.business.business_id,
        category_id: Number(selectedCategoryId)
      });
      toast.success('Business category reassigned successfully!');
      setReassignModal({ show: false, business: null });
      fetchBusinesses();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reassign category');
    } finally {
      setProcessing(false);
    }
  };

  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string>('all');

  useEffect(() => {
    fetchCategories();
    fetchCountries();
    fetchBusinesses();
  }, []);

  const handleSelectBusiness = (businessId: number) => {
    setSelectedBusinesses(prev =>
      prev.includes(businessId) ? prev.filter(id => id !== businessId) : [...prev, businessId]
    );
  };

  const handleSelectAllBusinesses = () => {
    if (selectedBusinesses.length === businesses.length) {
      setSelectedBusinesses([]);
    } else {
      setSelectedBusinesses(businesses.map(b => b.business_id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      setProcessing(true);
      await adminService.bulkDeleteBusinesses(selectedBusinesses);
      toast.success('Selected businesses deleted successfully!');
      setSelectedBusinesses([]);
      setBulkDeleteModal(false);
      fetchBusinesses();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete businesses');
    } finally {
      setProcessing(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await adminService.getAllCategories();
      setCategories(data.filter(c => c.is_active === 1));
    } catch (error: any) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const data = await adminService.getBusinessCountries();
      setAvailableCountries(data || []);
    } catch (error: any) {
      console.error('Failed to fetch countries:', error);
      setAvailableCountries([]);
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchBusinesses = async (
    showToast = false, 
    pageNum = currentPage, 
    limitNum = pageSize,
    status = statusFilter,
    country = countryFilter,
    search = searchQuery
  ) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Optimized server-side pagination for 1M+ scale
      const params: GetBusinessesParams = {
        page: pageNum,
        limit: limitNum,
      };

      if (status && status !== 'all') {
        params.status = status as BusinessStatus;
      }

      if (country && country !== 'all') {
        params.country = country;
      }

      if (search && search.trim()) {
        params.search = search.trim();
      }

      const data = await adminService.getAllBusinesses(params);
      setBusinesses(data.businesses);
      setTotalBusinesses(data.pagination?.total_businesses || data.businesses.length);
      if (data.stats) {
        setStats(data.stats);
      } else {
        setStats({
          total: data.pagination?.total_businesses || data.businesses.length,
          pending: data.businesses.filter(b => b.status === 'pending_approval').length,
          active: data.businesses.filter(b => b.status === 'active' || b.status === 'approved').length,
          suspended: data.businesses.filter(b => b.status === 'suspended').length
        });
      }
      setSelectedBusinesses([]); // Reset selection on reload

      if (showToast) {
        toast.success('Businesses refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch businesses');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchBusinesses(false, newPage, pageSize, statusFilter, countryFilter, searchQuery);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    fetchBusinesses(false, 1, newSize, statusFilter, countryFilter, searchQuery);
  };

  const handleFilterChange = (filterVal: string) => {
    setStatusFilter(filterVal);
    setCurrentPage(1);
    fetchBusinesses(false, 1, pageSize, filterVal, countryFilter, searchQuery);
  };

  const handleCountryFilterChange = (countryVal: string) => {
    setCountryFilter(countryVal);
    setCurrentPage(1);
    fetchBusinesses(false, 1, pageSize, statusFilter, countryVal, searchQuery);
  };

  const handleSearchChange = (term: string) => {
    setSearchQuery(term);
    setCurrentPage(1);
    fetchBusinesses(false, 1, pageSize, statusFilter, countryFilter, term);
  };



  const viewBusinessDetails = (businessId: number) => {
    navigate(`/admin/businesses/${businessId}`);
  };

  const handleApproveBusiness = async () => {
    if (!approveModal.business) return;

    try {
      setProcessing(true);
      await adminService.approveBusiness({ business_id: approveModal.business.business_id });
      toast.success('Business approved successfully!');
      setApproveModal({ show: false, business: null });
      fetchBusinesses();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve business');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectBusiness = async () => {
    if (!rejectModal.business || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setProcessing(true);
      await adminService.rejectBusiness({
        business_id: rejectModal.business.business_id,
        rejection_reason: rejectionReason
      });
      toast.success('Business rejected successfully!');
      setRejectModal({ show: false, business: null });
      setRejectionReason('');
      fetchBusinesses();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject business');
    } finally {
      setProcessing(false);
    }
  };

  const handleSuspendBusiness = async () => {
    if (!suspendModal.business || !suspensionReason.trim()) {
      toast.error('Please provide a suspension reason');
      return;
    }

    try {
      setProcessing(true);
      await adminService.suspendBusiness({
        business_id: suspendModal.business.business_id,
        suspension_reason: suspensionReason
      });
      toast.success('Business suspended successfully!');
      setSuspendModal({ show: false, business: null });
      setSuspensionReason('');
      fetchBusinesses();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to suspend business');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnsuspend = async (businessId: number) => {
    try {
      await adminService.unsuspendBusiness(businessId);
      toast.success('Business unsuspended successfully!');
      fetchBusinesses();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to unsuspend business');
    }
  };

  const handleVerify = async (businessId: number) => {
    try {
      await adminService.verifyBusiness(businessId);
      toast.success('Business verified successfully!');
      fetchBusinesses();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to verify business');
    }
  };


  const getStatusBadge = (status: BusinessStatus) => {
    const styles = {
      pending_approval: 'bg-warning-50 text-warning-600 border-warning-200',
      approved: 'bg-success-50 text-success-600 border-success-200',
      rejected: 'bg-danger-50 text-danger-600 border-danger-200',
      active: 'bg-success-50 text-success-600 border-success-200',
      suspended: 'bg-danger-50 text-danger-600 border-danger-200',
      paused: 'bg-secondary-50 text-secondary-600 border-secondary-200'
    };
    return styles[status] || 'bg-secondary-50 text-secondary-600 border-secondary-200';
  };

  const getStatusIcon = (status: BusinessStatus) => {
    switch (status) {
      case 'pending_approval': return <Clock size={16} />;
      case 'approved': return <CheckCircle size={16} />;
      case 'rejected': return <XCircle size={16} />;
      case 'active': return <CheckCircle size={16} />;
      case 'suspended': return <Ban size={16} />;
      case 'paused': return <Archive size={16} />;
      default: return <Store size={16} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Business Management</h1>
          <p className="text-secondary-600">Manage and approve business registrations</p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedBusinesses.length > 0 && (
            <button
              onClick={() => setBulkDeleteModal(true)}
              className="bg-danger-500 hover:bg-danger-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors animate-fade-in"
            >
              <Trash2 size={18} />
              <span>Delete Selected ({selectedBusinesses.length})</span>
            </button>
          )}
          <button
            onClick={() => fetchBusinesses(true)}
            disabled={refreshing}
            className="bg-secondary-50 hover:bg-secondary-100 text-secondary-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-primary-500">
          <p className="text-sm text-secondary-600 mb-2">Total Businesses</p>
          <p className="text-3xl font-bold text-primary-600">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-warning-500">
          <p className="text-sm text-secondary-600 mb-2">Pending Approval</p>
          <p className="text-3xl font-bold text-warning-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-success-500">
          <p className="text-sm text-secondary-600 mb-2">Active</p>
          <p className="text-3xl font-bold text-success-600">{stats.active}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-danger-500">
          <p className="text-sm text-secondary-600 mb-2">Suspended</p>
          <p className="text-3xl font-bold text-danger-600">{stats.suspended}</p>
        </div>
      </div>

      {/* Business List */}
      <DataTable
        data={businesses}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search by business name, owner, email..."
        searchFields={['business_name', 'owner_name', 'phone', 'email']}
        filterable={true}
        filterOptions={[
          { label: 'Pending Approval', value: 'pending_approval', field: 'status' },
          { label: 'Active', value: 'active', field: 'status' },
          { label: 'Approved', value: 'approved', field: 'status' },
          { label: 'Rejected', value: 'rejected', field: 'status' },
          { label: 'Suspended', value: 'suspended', field: 'status' }
        ]}
        customFilters={
          <div className="w-full sm:w-auto flex items-center">
            <div className="relative w-full sm:w-auto">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-secondary-400">
                <Globe size={16} />
              </div>
              <select
                value={countryFilter}
                onChange={(e) => handleCountryFilterChange(e.target.value)}
                className="w-full sm:w-auto pl-9 pr-8 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-sm text-secondary-800 font-medium cursor-pointer"
              >
                <option value="all">All Countries</option>
                {availableCountries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>
        }
        defaultPageSize={pageSize}
        pageSizeOptions={[10, 20, 50, 100]}
        totalCount={totalBusinesses}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        emptyMessage="No businesses found"
        emptyIcon={<Store size={64} className="text-secondary-300 mx-auto mb-4" />}
        columns={[
          {
            header: (
              <input
                type="checkbox"
                checked={selectedBusinesses.length === businesses.length && businesses.length > 0}
                onChange={handleSelectAllBusinesses}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500 w-4 h-4 cursor-pointer"
              />
            ),
            accessor: (business) => (
              <input
                type="checkbox"
                checked={selectedBusinesses.includes(business.business_id)}
                onChange={() => handleSelectBusiness(business.business_id)}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500 w-4 h-4 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              />
            ),
            width: '40px',
          },
          {
            header: 'Business',
            accessor: (business) => {
              const logoUrl = getImageUrl(business.logo_url);
              const locationParts = [business.city, business.state, business.country].filter(Boolean);
              return (
                <div className="flex items-center space-x-3">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={business.business_name}
                      className="w-12 h-12 rounded-lg object-cover border border-secondary-200 shadow-sm hover:scale-105 transition-all duration-200"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const fallback = parent.querySelector('.fallback-avatar');
                          if (fallback) (fallback as HTMLElement).style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  {(!logoUrl || !business.logo_url?.trim()) && (
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center border border-primary-200">
                      <Store className="text-primary-600" size={24} />
                    </div>
                  )}
                  {logoUrl && (
                    <div className="fallback-avatar w-12 h-12 bg-primary-100 rounded-lg hidden items-center justify-center border border-primary-200">
                      <Store className="text-primary-600" size={24} />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-secondary-900">{business.business_name}</p>
                    <p className="text-xs text-secondary-500 flex items-center space-x-1 mt-1">
                      <MapPin size={12} />
                      <span>{locationParts.length > 0 ? locationParts.join(', ') : 'N/A'}</span>
                    </p>
                  </div>
                </div>
              );
            },
            sortable: true,
          },
          {
            header: 'Owner',
            accessor: (business) => (
              <div>
                <p className="text-sm font-medium text-secondary-900">{business.owner_name}</p>
                <p className="text-xs text-secondary-500 flex items-center space-x-1 mt-1">
                  <Phone size={12} />
                  <span>{business.phone}</span>
                </p>
              </div>
            ),
            sortable: true,
          },
          {
            header: 'Category',
            accessor: (business) => (
              <span className="text-sm font-medium text-secondary-700">{business.category_name}</span>
            ),
            sortable: true,
          },
          {
            header: 'Status',
            accessor: (business) => (
              <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(business.status)}`}>
                {getStatusIcon(business.status)}
                <span className="capitalize">{business.status.replace('_', ' ')}</span>
              </span>
            ),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Rating',
            accessor: (business) => (
              <div className="flex items-center justify-center space-x-1">
                <Star size={14} className="text-warning-500 fill-warning-500" />
                <span className="text-sm font-semibold">{business.avg_rating}</span>
                <span className="text-xs text-secondary-500">({business.total_reviews})</span>
              </div>
            ),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Revenue',
            accessor: (business) => (
              <div>
                <p className="font-semibold text-secondary-900">
                  {business.total_revenue || '$0.00'}
                </p>
                <p className="text-xs text-secondary-500">{business.total_transactions || 0} transactions</p>
              </div>
            ),
            align: 'center',
            sortable: true,
          },
        ]}
        renderActions={(business) => (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                viewBusinessDetails(business.business_id);
              }}
              className="bg-primary-50 hover:bg-primary-100 text-primary-600 p-2 rounded-lg transition-colors"
              title="View Details"
            >
              <Eye size={18} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openReassignModal(business);
              }}
              className="bg-purple-50 hover:bg-purple-100 text-purple-600 p-2 rounded-lg transition-colors"
              title="Reassign Category"
            >
              <Tag size={18} />
            </button>
            {business.status === 'pending_approval' && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setApproveModal({ show: true, business });
                  }}
                  className="bg-success-50 hover:bg-success-100 text-success-600 p-2 rounded-lg transition-colors"
                  title="Approve"
                >
                  <CheckCircle size={18} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setRejectModal({ show: true, business });
                  }}
                  className="bg-danger-50 hover:bg-danger-100 text-danger-600 p-2 rounded-lg transition-colors"
                  title="Reject"
                >
                  <XCircle size={18} />
                </button>
              </>
            )}
            {(business.status === 'active' || business.status === 'approved') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSuspendModal({ show: true, business });
                }}
                className="bg-warning-50 hover:bg-warning-100 text-warning-600 p-2 rounded-lg transition-colors"
                title="Suspend"
              >
                <Ban size={18} />
              </button>
            )}
            {business.status === 'suspended' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnsuspend(business.business_id);
                }}
                className="bg-success-50 hover:bg-success-100 text-success-600 p-2 rounded-lg transition-colors"
                title="Unsuspend"
              >
                <CheckCircle size={18} />
              </button>
            )}
          </div>
        )}
      />

      {/* Approve Modal */}
      {approveModal.show && approveModal.business && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-success-100 p-3 rounded-full">
                  <CheckCircle className="text-success-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Approve Business</h2>
              </div>
              <button onClick={() => setApproveModal({ show: false, business: null })} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-secondary-700 mb-4">
                Are you sure you want to approve <strong>{approveModal.business.business_name}</strong>?
              </p>
              <p className="text-sm text-secondary-600">
                The business owner will be notified and their business will become active on the platform.
              </p>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setApproveModal({ show: false, business: null })}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveBusiness}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-success-500 hover:bg-success-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Approving...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    <span>Approve Business</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal.show && rejectModal.business && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-danger-100 p-3 rounded-full">
                  <XCircle className="text-danger-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Reject Business</h2>
              </div>
              <button onClick={() => setRejectModal({ show: false, business: null })} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-secondary-700">
                Rejecting <strong>{rejectModal.business.business_name}</strong>
              </p>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Rejection Reason <span className="text-danger-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  placeholder="Provide a clear reason for rejection..."
                  rows={4}
                  required
                />
              </div>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setRejectModal({ show: false, business: null })}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectBusiness}
                disabled={processing || !rejectionReason.trim()}
                className="flex-1 px-6 py-3 bg-danger-500 hover:bg-danger-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Rejecting...</span>
                  </>
                ) : (
                  <>
                    <XCircle size={20} />
                    <span>Reject Business</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {suspendModal.show && suspendModal.business && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-warning-100 p-3 rounded-full">
                  <Ban className="text-warning-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Suspend Business</h2>
              </div>
              <button onClick={() => setSuspendModal({ show: false, business: null })} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-secondary-700">
                Suspending <strong>{suspendModal.business.business_name}</strong>
              </p>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Suspension Reason <span className="text-danger-500">*</span>
                </label>
                <textarea
                  value={suspensionReason}
                  onChange={(e) => setSuspensionReason(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  placeholder="Provide a clear reason for suspension..."
                  rows={4}
                  required
                />
              </div>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setSuspendModal({ show: false, business: null })}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSuspendBusiness}
                disabled={processing || !suspensionReason.trim()}
                className="flex-1 px-6 py-3 bg-warning-500 hover:bg-warning-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Suspending...</span>
                  </>
                ) : (
                  <>
                    <Ban size={20} />
                    <span>Suspend Business</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {bulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-danger-100 p-3 rounded-full">
                  <Trash2 className="text-danger-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Delete Businesses</h2>
              </div>
              <button onClick={() => setBulkDeleteModal(false)} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-secondary-700 mb-4">
                Are you sure you want to delete the <strong>{selectedBusinesses.length}</strong> selected businesses?
              </p>
              <p className="text-sm text-secondary-600">
                This action will mark the selected businesses as deleted and they will no longer be visible on the platform.
              </p>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setBulkDeleteModal(false)}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-danger-500 hover:bg-danger-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={20} />
                    <span>Delete Businesses</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Category Modal */}
      {reassignModal.show && reassignModal.business && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-purple-100 p-3 rounded-full">
                  <Tag className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900">Reassign Category</h2>
                  <p className="text-xs text-secondary-500">{reassignModal.business.business_name}</p>
                </div>
              </div>
              <button onClick={() => setReassignModal({ show: false, business: null })} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-1">
                  Current Category
                </label>
                <div className="px-4 py-2.5 bg-secondary-50 border border-secondary-200 rounded-lg text-secondary-800 font-medium text-sm">
                  {reassignModal.business.category_name || 'Unassigned'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-1">
                  Select New Category <span className="text-danger-500">*</span>
                </label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none text-secondary-900 font-medium"
                >
                  <option value="">Select New Category...</option>
                  {categories.map((cat) => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setReassignModal({ show: false, business: null })}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReassignCategory}
                disabled={processing || !selectedCategoryId || Number(selectedCategoryId) === reassignModal.business.business_category_id}
                className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Tag size={20} />
                    <span>Save Category</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessManagement;
